import {useEvent} from '@gaia/hooks/useEvent';
import convertLngLat from '@gaia/utils/mapbox/convertLngLat';
import {AreaPointPopup, TrackPointPopup} from '@map/popups/ControlPoint';
import type {Position} from 'geojson';
import {type Offset, Popup, type PopupOptions} from 'mapbox-gl';
import {type ReactNode, useEffect, useMemo, useRef, useState} from 'react';

export interface ControlPointPopupProps {
    onOverlaysChange: SetState<OverlaysState>;
    setSelections: SetState<SelectionState[]>;
    interaction: InteractionState;
    onInteractionChange: SetState<InteractionState>;
    overlays: OverlaysState;
    selection: SelectionState | undefined;
}

interface UseControlPointPopupArgs {
    map: MapboxMap | undefined;
    popupProps: ControlPointPopupProps;
}

const height = 14,
    width = 14;

const offset: Offset = {
    top: [0, 0],
    'top-left': [0, 0],
    'top-right': [0, 0],
    bottom: [0, -height],
    'bottom-left': [0, -height],
    'bottom-right': [0, -height],
    right: [-(width / 2), -(height / 2)],
    left: [width / 2, -(height / 2)],
};

const popupOptions: PopupOptions = {
    offset,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 'none',
};

export function useControlPointPopup({
    map,
    popupProps,
}: UseControlPointPopupArgs): [ReactNode, HTMLElement] {
    const {selection} = popupProps;

    const popupInstanceRef = useRef<Popup>();
    const [popupComponent, setPopupComponent] = useState<ReactNode>();
    const popupContainer = useMemo(() => document.createElement('div'), []);
    const popupLocation = getPopupLocation(popupProps);

    const onSelectionChanged = useEvent(() => {
        if (!map || !popupLocation || selection?.type !== 'waypoint') return;

        popupInstanceRef.current = new Popup(popupOptions);
        popupInstanceRef.current.setDOMContent(popupContainer);
        popupInstanceRef.current.addTo(map);
        popupInstanceRef.current.setLngLat(convertLngLat(popupLocation));

        setPopupComponent(renderPopup(popupProps));
    });

    useEffect(() => {
        if (popupLocation) popupInstanceRef.current?.setLngLat(convertLngLat(popupLocation));
    }, [popupLocation]);

    useEffect(() => {
        onSelectionChanged();
        return () => {
            popupInstanceRef.current?.remove();
            popupInstanceRef.current = undefined;
            setPopupComponent(undefined);
        };
    }, [selection, onSelectionChanged]);

    return [popupComponent, popupContainer];
}

function getPopupLocation(props: ControlPointPopupProps): Position | undefined {
    const {selection, interaction} = props;

    if (selection?.type !== 'waypoint') return undefined;

    switch (interaction.type) {
        case 'editingRoute': {
            if (!interaction.track.isGeometryLoaded) return undefined;
            return interaction.track.points[selection.localId]?.geometry.coordinates;
        }
        case 'editingArea': {
            if (!selection.areaLocalId) return undefined;
            if (!('points' in interaction.area)) return undefined;
            return interaction.area.points[selection.localId]?.geometry.coordinates;
        }
        default: {
            return undefined;
        }
    }
}

function renderPopup(props: ControlPointPopupProps): ReactNode {
    const {selection, interaction} = props;

    if (selection?.type !== 'waypoint') return undefined;

    switch (interaction.type) {
        case 'editingRoute': {
            if (!interaction.track.isGeometryLoaded) return undefined;
            if (!interaction.track.points[selection.localId]) return undefined;
            return <TrackPointPopup {...props} selection={selection} />;
        }
        case 'editingArea': {
            if (!('points' in interaction.area)) return undefined;
            if (!interaction.area.points[selection.localId]) return undefined;
            return <AreaPointPopup {...props} selection={selection} />;
        }
        default: {
            return undefined;
        }
    }
}
